<template>
  <ComDialog
    :isShow="dialogVisible"
    :width="647"
    :title="t('安装应用')"
    alignCenter
    @close="handleClose"
  >
    <div class="upload-container">
          <el-upload
            class="upload-content"
            drag
            multiple
            :disabled="uploading"
            :auto-upload="false"
            v-model:file-list="fileList"
            :limit="5"
            accept=".apk"
            :on-change="handleFileChange"
          >
            <el-icon class="el-icon--upload"><upload-filled /></el-icon>
            <div class="el-upload__text">
              拖拽文件到此处或 <em>点击上传文件</em>
            </div>
             <template #tip>
              <div class="el-upload__tip">
                支持多文件上传，只支持上传安装.apk结尾的应用包
              </div>
            </template>
          </el-upload>
      
      <!-- 上传文件列表展示 -->
      <div class="upload-files" v-if="uploadFiles.length">
        <div v-for="file in uploadFiles" :key="file.uid" class="file-item">
          <div class="file-header">
            <span class="file-name">{{ file.name }}</span>
            <span class="file-size">{{ formatSize(file.size) }}</span>
            <span class="file-status">{{ getStatusText(file) }}</span>
          </div>
          
          <div v-if="file.calculating" class="calculating">
            <el-icon class="is-loading"><loading /></el-icon>
            <span>计算MD5中... {{ file.md5Progress }}%</span>
          </div>
          
          <el-progress
            v-else
            :percentage="file.progress"
            :status="getProgressStatus(file)"
            :stroke-width="16"
            :text-inside="true"
          />
          
          <div class="file-actions">
            <el-button
              v-if="file.status === 'uploading'"
              size="small"
              @click="pauseFile(file)"
            >
              暂停
            </el-button>
            <el-button
              v-if="file.status === 'paused'"
              size="small"
              type="success"
              @click="resumeFile(file)"
            >
              继续
            </el-button>
            <el-button
              v-if="file.status === 'failed'"
              size="small"
              type="warning"
              @click="retryFile(file)"
            >
              重试
            </el-button>
          </div>
        </div>
      </div>
      
      <div class="upload-actions">
        <el-button
          type="primary"
          :disabled="!canUpload"
          :loading="uploading"
          @click="startUpload"
        >
          {{ uploading ? '上传中' : '开始上传' }}
        </el-button>
        <el-button
          v-if="uploading"
          @click="cancelUpload"
        >
          取消上传
        </el-button>
      </div>
    </div>
  </ComDialog>
</template>

<script setup>
import { ref, computed, watch, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import { UploadFilled, Loading } from '@element-plus/icons-vue'
import SparkMD5 from 'spark-md5'
import axios from 'axios'
import request from '@/utils/request'
import API_CONFIG from '../api.json'

const { t } = useI18n()
const emit = defineEmits(['freshList'])

// 对话框控制
const dialogVisible = ref(false)
const open = (data) => {
  userId.value = data[0].userId
  productList.value = data
  dialogVisible.value = true
  resetState()
}

const handleClose = () => {
  if (!uploading.value) {
    dialogVisible.value = false
  } else {
    ElMessage.warning('上传正在进行中，请先取消上传')
  }
}

// 用户和设备选择
const userList = ref([])
const productList = ref([])
const userId = ref('')
const loading = ref(false)

// 文件上传相关
const fileList = ref([])
const uploadFiles = ref([])
const uploading = ref(false)
const cancelTokens = ref([])

// 上传配置
const CHUNK_SIZE = 5 * 1024 * 1024 // 5MB分片大小
const CONCURRENT_UPLOADS = 3 // 并发上传数
const MAX_RETRY_NUMS = 0 // 最大重试次数

// 计算属性
const canUpload = computed(() => {
  return (
    !uploading.value &&
    fileList.value.length > 0 &&
    productList.value.length > 0
  )
})

const handleFileChange = (file, files) => {
  if(!file.name.includes('.apk')){
    ElMessage.warning('只能上传.apk结尾的应用包')
    return 
  }
  fileList.value = files
}

const formatSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// 新增状态校验函数，设置状态函数
const validStatuses = [
  "calculating",
  "pending",
  "uploading",
  "success",
  "failed",
  "paused",
];
const setFileStatus = (file, status) => {
  if (validStatuses.includes(status)) {
    file.status = status;
  } else {
    file.status = "failed";
  }
};

const getStatusText = (file) => {
  switch (file.status) {
    case "calculating":
      return "计算MD5中";
    case "pending":
      return "等待上传";
    case "uploading":
      return "上传中";
    case "success":
      return "上传完成";
    case "failed":
      return "上传失败";
    case "paused":
      return "已暂停";
    default:
      return "";
  }
};

const getProgressStatus = (file) => {
  switch (file.status) {
    case "success":
      return "success";
    case "failed":
      return "exception";
    case "paused":
      return "warning";
    default:
      return undefined;
  }
};

// 动态计算分片大小
const getCHUNK_SIZE = (fileSize) => {
  // 定义各阶段的大小阈值（单位：字节）
  const SIZE_50M = 50 * 1024 * 1024; // 50MB
  const SIZE_100M = 100 * 1024 * 1024; // 100MB
  const SIZE_300M = 300 * 1024 * 1024; // 300MB

  // 定义各阶段对应的分片大小（单位：字节）
  const CHUNK_5M = 5 * 1024 * 1024; // 5MB
  const CHUNK_10M = 10 * 1024 * 1024; // 10MB
  const CHUNK_15M = 15 * 1024 * 1024; // 15MB
  const CHUNK_20M = 20 * 1024 * 1024; // 20MB

  // 根据文件大小返回对应的分片大小
  if (fileSize <= SIZE_50M) {
    return CHUNK_5M;
  } else if (fileSize <= SIZE_100M) {
    return CHUNK_10M;
  } else if (fileSize <= SIZE_300M) {
    return CHUNK_10M;
  } else {
    return CHUNK_10M;
  }
};

// 计算合并后的分片信息（处理最后一片<5M的情况）
const getMergedChunksInfo = (fileSize) => {
  const BASE_CHUNK_SIZE = getCHUNK_SIZE(fileSize);
  const MIN_LAST_CHUNK_SIZE = 5 * 1024 * 1024; // 最后一片最小阈值：5MB
  let totalChunks = Math.ceil(fileSize / BASE_CHUNK_SIZE); // 初始总分片数
  let chunkSizes = new Array(totalChunks).fill(BASE_CHUNK_SIZE); // 初始各分片大小

  // 处理最后一片：若小于5M，与倒数第二片合并
  if (totalChunks >= 2) {
    // 至少有2片才需要合并
    const lastChunkStart = (totalChunks - 1) * BASE_CHUNK_SIZE;
    const lastChunkSize = fileSize - lastChunkStart; // 最后一片实际大小

    if (lastChunkSize < MIN_LAST_CHUNK_SIZE) {
      // 合并最后两片：总分片数减1，最后一片大小 = 原倒数第二片 + 原最后一片
      totalChunks -= 1;
      chunkSizes.pop();
      chunkSizes[totalChunks - 1] = BASE_CHUNK_SIZE + lastChunkSize;
    }
  }

  // 返回：总分片数 + 每片的具体大小（用于后续分片切割）
  return { totalChunks, chunkSizes };
};

/**
 * 计算文件MD5
 * @param {File} file
 * @returns {Promise<string|null>}
 */
const calculateFileMD5 = (file, fileUid) => {
  return new Promise((resolve) => {
    const blobSlice =
      File.prototype.slice ||
      File.prototype.mozSlice ||
      File.prototype.webkitSlice;
    // 从uploadFiles中获取当前文件的合并后分片信息--- 计算之后的文件分片大小
    const currentFile = uploadFiles.value.find((f) => f.uid === fileUid);
    if (!currentFile) {
      resolve(null);
      return;
    }

    const { totalChunks, chunkSizes } = currentFile;
    let currentChunk = 0;
    const spark = new SparkMD5.ArrayBuffer();
    const fileReader = new FileReader();

    fileReader.onload = (e) => {
      spark.append(e.target.result);
      currentChunk++;

      // 更新MD5计算进度
      const fileIndex = uploadFiles.value.findIndex((f) => f.uid === file.uid);
      if (fileIndex !== -1) {
        uploadFiles.value[fileIndex].md5Progress = Math.floor(
          (currentChunk / totalChunks) * 100
        );
      }

      if (currentChunk < totalChunks) {
        loadNext();
      } else {
        resolve(spark.end());
      }
    };

    fileReader.onerror = () => {
      const fileIndex = uploadFiles.value.findIndex((f) => f.uid === file.uid);
      if (fileIndex !== -1) {
        uploadFiles.value[fileIndex].status = "failed";
        uploadFiles.value[fileIndex].calculating = false;
      }
      resolve(null);
    };

    function loadNext() {
      const start =
        currentChunk === 0
          ? 0
          : chunkSizes
              .slice(0, currentChunk)
              .reduce((sum, size) => sum + size, 0);
      // 当前分片的结束位置 = 起始位置 + 当前分片大小
      const end = start + chunkSizes[currentChunk];
      fileReader.readAsArrayBuffer(blobSlice.call(file, start, end));
    }
    loadNext();
  });
};

// 获取上传参数
const getUploadParams = async (params) => {
  const { md5, fileName, totalChunks, fileSuffix, totalSize } = params;
  try {
    const res = await request.post(API_CONFIG.getUploadFileParameter.url, {
      md5,
      fileName,
      totalChunks,
      fileSuffix,
      totalSize,
    });
    if (res.code === 200) {
      return res.data.preUploadResponse;
    } else {
      throw new Error(res.message || "获取上传参数失败");
    }
  } catch (error) {
    console.error("获取上传参数失败:", error);
    throw error;
  }
};

// 开始上传
const startUpload = async () => {
  if (productList.value.length === 0) {
    ElMessage.warning("请选择设备");
    return;
  }

  uploading.value = true;
  uploadFiles.value = fileList.value.map((file) => {
    const { totalChunks, chunkSizes } = getMergedChunksInfo(file.size); // 获取合并后的分片信息
    return {
      ...file,
      uid: file.uid,
      status: "calculating",
      calculating: true,
      md5Progress: 0,
      progress: 0,
      uploadedChunks: [],
      chunkSizes: chunkSizes, // 每个文件独立分片大小
      totalChunks: totalChunks,
      chunks: {},
      cancelToken: null,
      uploadParams: null, // 新增字段保存接口参数
    };
  });

  // 为每个文件计算MD5并检查是否存在
  for (const file of uploadFiles.value) {
    try {
      file.md5 = await calculateFileMD5(file.raw, file.uid);
      file.calculating = false;
      // 获取上传参数
      const params = await getUploadParams({
        md5: file.md5,
        fileName: file.name,
        totalChunks: file.totalChunks,
        fileSuffix: file.name.match(/\.([^.]+)$/)[0],
        totalSize: file.size,
      });
      file.uploadParams = params; // 保存接口参数
      // 新增：判断status
      if (params.status == 1) {
        // 文件已存在，模拟进度条加载
        setFileStatus(file, "uploading");
        file.progress = 0;
        // 模拟进度条动画
        await new Promise((resolve) => {
          let progress = 0;
          const interval = setInterval(() => {
            progress += 30;
            file.progress = Math.min(progress, 100);
            if (progress >= 100) {
              clearInterval(interval);
              resolve();
            }
          }, 100);
        });
        setFileStatus(file, "success");
        file.progress = 100;
        // 推送到设备
        await pushFileToDevice(
          {
            ctrAliaList: productList.value.map((item) => item.code),
            md5: file.md5,
            targetPath: params.url,
            installFlag: file.name.includes("apk"),
          },
          file
        );
        await recordUploadFile({
          fileName: file.name,
          fileUrl: params.url, // 假设接口返回url字段
          storage: file.size / 1024,
          fileType: getFileType(file.name),
          fileMd5: file.md5,
          userId: userId.value,
        });
      } else {
        file.progress = 0;
        setFileStatus(file, "pending");
      }
    } catch (error) {
      setFileStatus(file, "failed");
      file.calculating = false;
      console.error(`文件 ${file.name} 处理失败:`, error);
    }
  }

  // 执行完上面的文件的预分片之后，执行文件队列上传【上述可能存在场景文件已经存在，最终通过队列执行检验所有文件】
  processUploadQueue();
};

// 处理上传队列
const processUploadQueue = async () => {
  // 只处理非暂停状态的文件
  const pendingFiles = uploadFiles.value.filter((file) => {
    const isPending = ["pending", "failed"].includes(file.status);
    const isUnfinished = file.uploadedChunks.length < file.totalChunks;
    return isPending && isUnfinished;
  });

  if (pendingFiles.length === 0) {
    const hasPausedFile = uploadFiles.value.some(
      (file) => file.status === "paused"
    );
    if (!hasPausedFile) {
      // 只有无暂停文件时才检查完成
      checkAllFilesComplete();
    }
    return;
  }

  // 并发处理每个文件的上传任务，每个文件分片串行
  await Promise.all(
    pendingFiles.map((file) => {
      return uploadFileChunks(file);
    })
  );

  // 关键：所有待上传文件处理完毕（成功/失败），统一触发一次检查
  const hasPausedFile = uploadFiles.value.some(
    (file) => file.status === "paused"
  );
  if (!hasPausedFile) {
    checkAllFilesComplete();
  }
};

// 串行上传单个文件的所有分片
const uploadFileChunks = async (file) => {
  if (!file.status || file.status === "paused") return;
  setFileStatus(file, "uploading");

  for (let chunkIndex = 0; chunkIndex < file.totalChunks; chunkIndex++) {
    if (file.status === "paused") {
      return; // 暂停状态直接返回，不修改为失败
    }

    if (file.status === "failed") {
      return;
    }

    if (file.status === "success") {
      return;
    }

    if (!file.uploadedChunks.includes(chunkIndex) && file.status !== "failed") {
      await uploadChunk(file, chunkIndex);
    }
  }
};

/**
 * 上传分片
 * @param {UploadFile} file
 * @param {number} chunkIndex
 * @param {number} retryCount
 * @returns {Promise<void>}
 */
const uploadChunk = async (file, chunkIndex, retryCount = 0) => {
  if (!file.status || file.status === "paused") return;
  const chunkSize = file.chunkSizes[chunkIndex];
  // 计算当前分片的起始位置（累加前chunkIndex片的大小）
  const start =
    chunkIndex === 0
      ? 0
      : file.chunkSizes
          .slice(0, chunkIndex)
          .reduce((sum, size) => sum + size, 0);
  const end = Math.min(start + chunkSize, file.size);
  const chunk = file.raw.slice(start, end);

  const formData = new FormData();
  formData.append("md5", file.md5);
  formData.append("partNumber", chunkIndex);
  formData.append("totalChunks", file.totalChunks);
  formData.append("fileName", file.name);
  formData.append("file", chunk);
  formData.append("fileSuffix", file.name.match(/\.([^.]+)$/)[0]);
  formData.append("totalSize", file.size);
  formData.append("token", file.uploadParams?.token);

  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();
  file.chunks[chunkIndex] = { source };
  cancelTokens.value.push(source);

  try {
    // 使用每个文件的专属上传接口和token
    const uploadUrl = file.uploadParams?.uploadUrl;
    const token = file.uploadParams?.token;
    const headers = {
      "Content-Type": "multipart/form-data",
      repeatSubmit: false,
    };
    if (token) headers["X-Token"] = token;

    // 上传前再次判断是否暂停
    if (file.status === "paused") return;
    const res = await axios.post(uploadUrl, formData, {
      headers,
      cancelToken: source.token,
      onUploadProgress: (progressEvent) => {
        // 新增：检查文件是否已暂停，如果已暂停则不再更新进度
        if (file.status === "paused") return;

        if (progressEvent.lengthComputable) {
          // 更精确计算整体文件进度
          const uploadedChunks = file.uploadedChunks.length;
          const currentChunkPercent = Math.round(
            (progressEvent.loaded / progressEvent.total) * 100
          );
          // 已完成分片的总大小 + 当前分片已上传大小 = 总上传大小
          const uploadedTotalSize = file.uploadedChunks.reduce(
            (sum, idx) => sum + file.chunkSizes[idx],
            0
          );
          const currentChunkUploadedSize =
            (progressEvent.loaded / progressEvent.total) * chunkSize;
          file.progress = Math.min(
            100,
            Math.round(
              ((uploadedTotalSize + currentChunkUploadedSize) / file.size) * 100
            )
          );
        }
      },
    });

    // 检查是否分片已存在（status=1）
    if (res.data.data && res.data.data.status == 1) {
      // 文件已存在，立即完成上传，并记录文件信息
      file.progress = 100;
      setFileStatus(file, "success");
      // 推送到设备
      await pushFileToDevice(
        {
          ctrAliaList: productList.value.map((item) => item.code),
          md5: file.md5,
          targetPath: res.data.data.url,
          installFlag: file.name.includes("apk"),
        },
        file
      );
      await recordUploadFile({
        fileName: file.name,
        fileUrl: res.data.data.url,
        storage: file.size / 1024,
        fileType: getFileType(file.name),
        fileMd5: file.md5,
        userId: userId.value,
      });

      // 取消当前文件的其他分片上传（如果有）
      if (file.chunks && Object.keys(file.chunks).length > 0) {
        Object.entries(file.chunks).forEach(([idx, chunk]) => {
          if (
            Number.parseInt(idx) !== chunkIndex &&
            chunk.source &&
            chunk.source.cancel
          ) {
            chunk.source.cancel("文件已存在，取消其他分片上传");
          }
        });
      }

      return; // 终止后续执行
    }

    if (!file.uploadedChunks.includes(chunkIndex)) {
      file.uploadedChunks.push(chunkIndex);
    }

    if (file.uploadedChunks.length === file.totalChunks) {
      file.progress = 100;
      setFileStatus(file, "success");
      await completeFileUpload(res.data.data, file);
      processUploadQueue();
    }
  } catch (error) {
    if (axios.isCancel(error)) {
      // 取消时保持当前状态（如果是暂停状态则不修改）
      if (file.status !== "paused") {
        setFileStatus(file, "paused"); // 确保取消后是暂停状态而非失败
      }
      return; // 取消属于正常流程，不触发重试
    }

    // 只有非暂停状态才处理错误和重试
    if (file.status !== "paused") {
      if (retryCount < MAX_RETRY_NUMS) {
        await uploadChunk(file, chunkIndex, retryCount + 1);
      } else {
        setFileStatus(file, "failed");
        file.errorMsg = error?.message || "分片上传失败";
        console.error(`分片 ${chunkIndex} 上传失败:`, error);
      }
    }
  } finally {
    cancelTokens.value = cancelTokens.value.filter((token) => token !== source);
    delete file.chunks[chunkIndex]; // 释放资源
  }
};

// 完成文件上传
const completeFileUpload = async (data, file) => {
  try {
    if (data.status == 1) {
      // 推送到设备
      await pushFileToDevice(
        {
          ctrAliaList: productList.value.map((item) => item.code),
          md5: file.md5,
          targetPath: data.url,
          installFlag: file.name.includes("apk"),
        },
        file
      );

      // 记录上传信息
      await recordUploadFile({
        fileName: file.name,
        fileUrl: data.url,
        storage: file.size / 1024,
        fileType: getFileType(file.name),
        fileMd5: file.md5,
        userId: userId.value,
      });
      ElMessage.success(`文件 ${file.name} 上传成功`);
    }
  } catch (error) {
    console.error(`文件 ${file.name} 合并失败:`, error);
    file.status = "failed";
  }
};

// 检查所有文件是否完成上传
const hasEmittedFreshList = ref(false);
const checkAllFilesComplete = () => {
  // 重置标记条件：只要有文件处于上传中，就重置标记
  if (hasEmittedFreshList.value) return;
  const allCompleted = uploadFiles.value.every(
    (file) => file.status === "success" || file.status === "failed"
  );
  // 修正：判断是否"没有文件在处理中"
  const hasPausedFile = uploadFiles.value.some(
    (file) => file.status === "paused"
  );
  const noProcessing =
    !uploadFiles.value.some((file) =>
      ["calculating", "uploading", "pending"].includes(file.status)
    ) && !hasPausedFile; // 有暂停文件则不满足"无处理中"条件

  if (allCompleted && noProcessing) {
    uploading.value = false;
    const successCount = uploadFiles.value.filter(
      (f) => f.status === "success"
    ).length;

    if (successCount === uploadFiles.value.length) {
      ElMessage.success("所有文件上传完成，可查看设备内安装结果或者去客户文件管理查看安装结果");
      emit("freshList");
      resetState(); // 自动清空
      hasEmittedFreshList.value = true;
      dialogVisible.value = false;
    } else {
      ElMessage.warning(
        `上传完成，但有${uploadFiles.value.length - successCount}个文件失败`
      );
      hasEmittedFreshList.value = false;
    }
  }
};

// 获取文件类型
const getFileType = (fileName) => {
  const suffix = fileName.match(/\.([^.]+)$/)?.[1]?.toLowerCase();
  if (suffix === "apk") return "1"; // 应用
  if (["png", "jpg", "jpeg", "webp", "gif"].includes(suffix)) return "3"; // 图片
  return "2"; // 文件
};

// 推送文件到设备
const pushFileToDevice = async (data, file) => {
  const params = {
    ctrAliaList: data.ctrAliaList,
    fileInfoList: [
      {
        fileName: file.name,
        md5: data.md5,
      },
    ],
    targetPath: "",
    installFlag: data.installFlag,
  };
  try {
    const res = await request.post(API_CONFIG.pushFile.url, params);
    if (res.code !==200) {
      uploading.value = false;
      throw new Error(res.message || "推送文件失败");
    }
  } catch (error) {
    console.error("推送文件失败:", error);
    uploading.value = false;
    throw error;
  }
};

// 记录上传文件信息（仅设备管理列表入口携带 userId 时记录云盘；直连页无 userId 则跳过）
const recordUploadFile = async (data) => {
  if (!userId.value) return
  try {
    const res = await request.post(API_CONFIG.recordUploadFileInfo.url, {
      cloudDiskUserFileList: [data],
    });
    if (res.code !== 200) {
      uploading.value = false;
      throw new Error(res.message || "记录上传信息失败");
    }
  } catch (error) {
    console.error("记录上传信息失败:", error);
    // 查找对应的文件并更新状态
    const targetFile = uploadFiles.value.find((f) => f.md5 === data.fileMd5);
    if (targetFile) {
      setFileStatus(targetFile, "failed");
      targetFile.errorMsg = "记录文件信息失败，请重试";
    }
    throw error;
  }
};

// 暂停文件上传
const pauseFile = (file) => {
  setFileStatus(file, "paused");
  // 取消当前文件所有正在上传的分片请求
  if (file.chunks && Object.keys(file.chunks).length > 0) {
    Object.values(file.chunks).forEach((chunk) => {
      if (chunk.source && chunk.source.cancel) {
        chunk.source.cancel("用户主动暂停上传");
      }
      // 从全局cancelTokens中移除已取消的token
      cancelTokens.value = cancelTokens.value.filter(
        (token) => token !== chunk.source
      );
    });
  }

  // 清除文件的chunks引用，释放资源
  file.chunks = {};

  // 更新全局上传状态（如果所有文件都已暂停，则全局状态设为false）
  const allPausedOrCompleted = uploadFiles.value.every((f) =>
    ["paused", "success", "failed"].includes(f.status)
  );
  if (allPausedOrCompleted) {
    uploading.value = false;
  }
  ElMessage.info(`文件 "${file.name}" 已暂停上传`);
};

// 继续文件上传
const resumeFile = (file) => {
  setFileStatus(file, "uploading");
  file.progress =
    file.uploadedChunks.length > 0
      ? Math.round(
          (file.uploadedChunks.reduce(
            (sum, idx) => sum + file.chunkSizes[idx],
            0
          ) /
            file.size) *
            100
        )
      : 0;
  uploading.value = true;
  hasEmittedFreshList.value = false;

  uploadFileChunks(file).then(() => {
    processUploadQueue();
  });
};

// 重试文件上传
const retryFile = async (file) => {
  const { totalChunks, chunkSizes } = getMergedChunksInfo(file.size);
  setFileStatus(file, "uploading");

  file.progress = 0;
  file.uploadedChunks = [];
  file.totalChunks = totalChunks;
  file.chunkSizes = chunkSizes;
  file.chunks = {}; // 清空旧的cancelToken

  uploading.value = true;
  hasEmittedFreshList.value = false;
  // 重新上传所有分片
  for (let chunkIndex = 0; chunkIndex < file.totalChunks; chunkIndex++) {
    if (file.status !== "failed") {
      await uploadChunk(file, chunkIndex);
    }
  }
};

// 取消上传
const cancelUpload = () => {
  cancelTokens.value.forEach((source) => {
    source.cancel("用户取消上传");
  });
  cancelTokens.value = [];

  uploadFiles.value.forEach((file) => {
    if (file.status === "uploading") {
      setFileStatus(file, "paused");
    }
  });

  uploading.value = false;
  ElMessage.warning("上传已取消");
};

// 重置状态
const resetState = () => {
  fileList.value = [];
  uploadFiles.value = [];
  uploading.value = false;
  cancelTokens.value = [];
  hasEmittedFreshList.value = false;
};

// 组件卸载时清理所有请求和定时器
onUnmounted(() => {
  cancelTokens.value.forEach((source) => source.cancel("组件卸载，取消上传"));
  cancelTokens.value = [];
  resetState();
});

// 监听 fileList 变化，同步 uploadFiles
watch(fileList, (newList) => {
  const newUids = newList.map((f) => f.uid);
  uploadFiles.value = uploadFiles.value.filter((f) => newUids.includes(f.uid));
});

defineExpose({ open });
</script>

<style scoped>
.upload-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.upload-content {
  width: 100%;
}

.upload-files {
  margin-top: 20px;
  max-height: 400px;
  overflow-y: auto;
}

.file-item {
  padding: 12px;
  margin-bottom: 12px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #fafafa;
}

.file-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.file-name {
  flex: 1;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-size {
  margin: 0 12px;
  color: #909399;
  font-size: 12px;
}

.file-status {
  color: #909399;
  font-size: 12px;
}

.calculating {
  display: flex;
  align-items: center;
  color: #409eff;
  margin: 8px 0;
}

.calculating .el-icon {
  margin-right: 6px;
  animation: rotating 2s linear infinite;
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.upload-actions {
  margin-top: 20px;
  text-align: right;
}
</style>